class InputQty extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('.input-qty__input');
    this.buttons = this.querySelectorAll('.input-qty__btn');
    this.minusButton = this.querySelector('.input-qty__btn--minus');
    this.plusButton = this.querySelector('.input-qty__btn--plus');
    this.onChangeCallback = null;

    this.handleButtonClick = this.onButtonClick.bind(this);
    this.handleInputChange = this.onInputChange.bind(this);
  }

  connectedCallback() {
    if (!this.input) return;

    this.buttons.forEach((button) => {
      button.addEventListener('click', this.handleButtonClick);
    });

    this.input.addEventListener('change', this.handleInputChange);
    this.normalizeInputValue();
    this.updateButtonStates();
  }

  disconnectedCallback() {
    if (!this.input) return;

    this.buttons.forEach((button) => {
      button.removeEventListener('click', this.handleButtonClick);
    });

    this.input.removeEventListener('change', this.handleInputChange);
  }

  onButtonClick(event) {
    event.preventDefault();
    const target = event.currentTarget;
    const action = target.dataset.action;
    const currentValue = this.getCurrentValue();
    const min = this.getMin();
    const max = this.getMax();

    let newValue = currentValue;

    if (action === 'increase') {
      if (max !== null) {
        newValue = Math.min(max, currentValue + 1);
      } else {
        newValue = currentValue + 1;
      }
    } else if (action === 'decrease') {
      newValue = Math.max(min, currentValue - 1);
    }

    if (newValue !== currentValue) {
      this.input.value = newValue;
      this.triggerChangeEvent();
      return;
    }

    this.updateButtonStates();
  }

  onInputChange() {
    let currentValue = this.getCurrentValue();
    const min = this.getMin();
    const max = this.getMax();

    if (Number.isNaN(currentValue) || currentValue < min) {
      currentValue = min;
    }

    if (max !== null && currentValue > max) {
      currentValue = max;
      this.showMaxQuantityModal(max);
    }

    this.input.value = String(currentValue);
    this.updateButtonStates();

    if (typeof this.onChangeCallback === 'function') {
      this.onChangeCallback(this.input.value);
    }
  }

  triggerChangeEvent() {
    // Dispatch a native change event on the input so forms can catch it
    const event = new Event('change', { bubbles: true });
    this.input.dispatchEvent(event);

    if (typeof this.onChangeCallback === 'function') {
      this.onChangeCallback(this.input.value);
    }
  }

  getMin() {
    const minValue = Number.parseInt(this.input.min || '1', 10);
    return Number.isFinite(minValue) ? minValue : 1;
  }

  getMax() {
    const rawMax = this.input.max;

    if (!rawMax) {
      return null;
    }

    const maxValue = Number.parseInt(rawMax, 10);
    return Number.isFinite(maxValue) ? maxValue : null;
  }

  getCurrentValue() {
    return Number.parseInt(this.input.value || '0', 10);
  }

  normalizeInputValue() {
    let currentValue = this.getCurrentValue();
    const min = this.getMin();
    const max = this.getMax();

    if (Number.isNaN(currentValue) || currentValue < min) {
      currentValue = min;
    }

    if (max !== null && currentValue > max) {
      currentValue = max;
    }

    this.input.value = String(currentValue);
  }

  updateButtonStates() {
    if (!this.input) {
      return;
    }

    const isInputDisabled = this.input.disabled;
    const min = this.getMin();
    const max = this.getMax();
    const currentValue = this.getCurrentValue();

    if (this.minusButton) {
      this.minusButton.disabled =
        isInputDisabled || Number.isNaN(currentValue) || currentValue <= min;
    }

    if (this.plusButton) {
      this.plusButton.disabled =
        isInputDisabled ||
        Number.isNaN(currentValue) ||
        (max !== null && currentValue >= max);
    }
  }

  showMaxQuantityModal(max) {
    const dialogModal = document.getElementById('DialogModal');

    if (
      !dialogModal ||
      typeof dialogModal.setContent !== 'function' ||
      typeof dialogModal.openModal !== 'function'
    ) {
      return;
    }

    const template = this.dataset.maxQuantityMessage || '';
    const message = template
      ? template.replace(/\{\{\s*max\s*\}\}/g, String(max))
      : `The maximum quantity you can add is ${max}.`;
    const safeMessage = this.escapeHtml(message);

    if (typeof dialogModal.setSize === 'function') {
      dialogModal.setSize('small');
    }

    dialogModal.setContent(`
      <div class="container text-center py-10 md:py-20">
        <p class="body-base text-text-dark">${safeMessage}</p>
      </div>
    `);
    dialogModal.openModal();
  }

  escapeHtml(value) {
    const text = String(value || '');
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}

if (!customElements.get('input-qty')) {
  customElements.define('input-qty', InputQty);
}
